import { getSessionSafe, sessionUncertain, signOutSafe, waitForSession } from "./supabase";
import { EARLY_STORE_KEY, type EarlyStore } from "@shared/earlyStore";
import { isLoginPage, loginPathFor } from "@shared/loginPath";
import { freshHeaders } from "@shared/freshPreview";
import { apiCacheRule, invalidatedBy } from "@shared/apiCacheRules";
import { copyOf, invalidateEntries, noteKey, readEntry, startApiCacheSync, writeEntry } from "./apiCache";
import type { ApiError, ApiResponse } from "@shared/types";
import { SLOW_REPORT_PATH, routePattern, slowKindFor, type CallOutcome } from "@shared/slowRequest";
import { currentHostMode } from "./host";

/**
 * Nothing the app fetches should be able to hang forever. A request with no
 * ceiling leaves its caller's spinner up indefinitely when a connection is
 * black-holed (proxy, sleeping laptop, dead tunnel) — the browser itself will
 * happily keep it pending for minutes. Uploads get a much longer leash because
 * they're genuinely slow on a phone connection.
 */
const REQUEST_TIMEOUT_MS = 20_000;
const UPLOAD_TIMEOUT_MS = 120_000;

/**
 * The active store id, sent as `X-Store-Id` on every authenticated request so
 * the backend scopes to the store the seller is currently viewing. Held at
 * module scope (not React state) so the plain `request()`/`upload()` helpers can
 * read it without threading context through every call. StoreContext keeps it in
 * sync via `setActiveStoreId`.
 */
let activeStoreId: string | null = null;
export function setActiveStoreId(id: string | null): void {
  activeStoreId = id;
}

/**
 * The advertiser door (117C S8) acts for the signed-in advertiser's own
 * account, not a store: while it is open, Iklan routes are told so with
 * `X-Ad-Account: 1` and no store id is sent.
 */
let adAccount = false;
export function setAdAccount(on: boolean): void {
  adAccount = on;
}

export class ApiClientError extends Error {
  code: string;
  status: number;
  details?: unknown;
  constructor(error: ApiError, status: number) {
    super(error.message);
    this.name = "ApiClientError";
    this.code = error.code;
    this.status = status;
    this.details = error.details;
  }
}

/**
 * A 401 from our own API is authoritative: the server only ever answers 401 when
 * the bearer token is missing or rejected (`requireAuth` / `requireSuperAdmin`) —
 * a caller who is merely not *allowed* to do something gets 403. So there is no
 * "permission 401" to protect: the token is dead, and staying put just leaves the
 * seller in a half-broken dashboard where every fetch fails, the sidebar collapses
 * to its capability-free items, and the KPI cards spin forever. Clear the session
 * and bounce to /login so they can sign in again.
 *
 * We deliberately do NOT re-check or refresh the session first. That check used to
 * live here, and on a poisoned token it was the hang: getSession()/signOut() both
 * queue on the same supabase auth lock a stalled refresh is holding.
 */
let handlingAuthFailure = false;

/**
 * Start the sign-out + redirect WITHOUT blocking the caller. Awaiting this from
 * `request()` is what made a 401 that answered in 24ms sit "pending" in the
 * network panel — and its caller's spinner up — for 15+ seconds. Fail fast: the
 * caller gets its 401 error immediately, the redirect lands a moment later.
 *
 * A dead session 401s several requests at once (the dashboard fires /profile,
 * /stores, /admin/session, /plans/me, list fetches… on load), so the burst
 * collapses into ONE sign-out.
 */
function handleAuthFailure(): void {
  if (handlingAuthFailure) return;
  handlingAuthFailure = true;
  void signOutSafe().then(() => {
    const { pathname, search } = window.location;
    if (!isLoginPage(pathname)) {
      const next = encodeURIComponent(pathname + search);
      window.location.assign(`${loginPathFor(pathname)}?next=${next}`);
    }
  });
}

/**
 * `fetch` with a hard ceiling, honouring the caller's own abort signal too. A
 * timeout surfaces as a normal ApiClientError so callers show their error state
 * instead of spinning.
 */
async function fetchWithTimeout(
  path: string,
  init: RequestInit,
  timeoutMs: number,
  external?: AbortSignal,
): Promise<Response> {
  const controller = new AbortController();
  const relayAbort = () => controller.abort();
  if (external) {
    if (external.aborted) controller.abort();
    else external.addEventListener("abort", relayAbort, { once: true });
  }
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);

  try {
    return await fetch(path, { ...init, signal: controller.signal });
  } catch (e) {
    if (timedOut) {
      throw new ApiClientError({ code: "timeout", message: "Request timed out" }, 408);
    }
    throw e;
  } finally {
    clearTimeout(timer);
    external?.removeEventListener("abort", relayAbort);
  }
}

/**
 * Human-readable message for a failed request. The server's `validate` middleware
 * already names the field and the reason in the message ("Penerangan: terlalu
 * panjang, maksimum 2,000 aksara", 118R); the field-level `details` are the
 * fallback for anything still answering a bare "Validation failed".
 */
export function apiErrorMessage(e: unknown, fallback = "Something went wrong"): string {
  if (e instanceof ApiClientError) {
    if (e.code === "validation_error" && e.message && e.message !== "Validation failed") return e.message;
    const d = e.details as
      | { fieldErrors?: Record<string, string[]>; formErrors?: string[] }
      | undefined;
    const field = d?.fieldErrors && Object.values(d.fieldErrors).flat().find(Boolean);
    const form = d?.formErrors?.find(Boolean);
    return field || form || e.message || fallback;
  }
  return fallback;
}

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  /** Skip attaching the auth token (for public endpoints). */
  public?: boolean;
  /**
   * Best-effort authenticated request: still attaches the token when a session
   * exists (so logged-in users get their data), but a 401 does NOT bounce to
   * /login. Use for optional buyer-context calls that run on PUBLIC pages — e.g.
   * a logged-out visitor's checkout should never be hijacked into a login page.
   */
  optionalAuth?: boolean;
  signal?: AbortSignal;
  /** A longer ceiling for a call known to be slow (an AI writing a page). */
  timeoutMs?: number;
  /** Extra headers, e.g. Bilik Coaching's room token. */
  headers?: Record<string, string>;
}

/**
 * The auth headers of the last authenticated request, kept so `unloadPost` can
 * fire during page teardown — where there is no time to await getSession().
 * Refreshed by every authenticated call, so it is as fresh as the session was
 * a moment ago.
 */
let lastAuthHeaders: Record<string, string> | null = null;

/** How long a request waits for a slow token refresh before giving up on it. */
const SLOW_REFRESH_WAIT_MS = 15_000;

/**
 * The session to send, telling "signed out" apart from "could not tell yet".
 *
 * A request used to go out with no token whenever getSessionSafe() answered null
 * — including when the refresh was merely slow (a tab back after an hour) or had
 * failed on the network. The server then said 401 and the 401 handler signed
 * the user out. Now an uncertain answer waits for the refresh instead. If it
 * lands, the request goes out normally. If the network is down, the caller gets
 * a retryable error and the session is kept. Only a refresh still stuck after
 * the whole wait — the poisoned-lock case the stall latch exists for — signs out,
 * since nothing else gets that browser out of it.
 */
async function sessionForRequest() {
  const session = await getSessionSafe();
  if (session || !sessionUncertain()) return session;
  const late = await waitForSession(SLOW_REFRESH_WAIT_MS);
  if (late) return late;
  if (sessionUncertain() === "stalled") {
    handleAuthFailure();
    throw new ApiClientError({ code: "unauthorized", message: "Session expired" }, 401);
  }
  throw new ApiClientError(
    { code: "session_unavailable", message: "Connection is slow. Please try again." },
    503,
  );
}

async function attachAuth(headers: Record<string, string>, optional = false): Promise<void> {
  // A best-effort call (public pages) never waits on or acts on the session.
  const session = optional ? await getSessionSafe() : await sessionForRequest();
  const token = session?.access_token;
  if (token) headers.Authorization = `Bearer ${token}`;
  if (adAccount) headers["X-Ad-Account"] = "1";
  else if (activeStoreId) headers["X-Store-Id"] = activeStoreId;
  lastAuthHeaders = token ? { ...headers } : null;
}

/**
 * Fire-and-forget POST that survives the page going away.
 *
 * `navigator.sendBeacon` cannot set headers, and this API authenticates with a
 * Bearer token plus X-Store-Id, so a beacon would simply 401. `keepalive` lets
 * the request outlive the document while still carrying both. Best-effort by
 * design: it returns false when no session has been seen, and a crashed or
 * backgrounded tab can still kill it in flight.
 */
export function unloadPost(path: string, body: unknown): boolean {
  if (!lastAuthHeaders) return false;
  try {
    void fetch(`/api${path}`, {
      method: "POST",
      headers: { ...lastAuthHeaders, "Content-Type": "application/json" },
      body: JSON.stringify(body),
      keepalive: true,
    }).catch(() => {});
    return true;
  } catch {
    return false;
  }
}

/**
 * The storefront root's store request, started by index.html's boot script before
 * this bundle had downloaded (shared/earlyStore.ts). Handed to the first public GET
 * for that very path, once; anything else, or a second ask, goes to the network.
 */
function takeEarly(path: string): Promise<Response> | null {
  if (typeof window === "undefined") return null;
  const slot = window as unknown as Record<string, EarlyStore | undefined>;
  const early = slot[EARLY_STORE_KEY];
  if (!early || early.path !== path) return null;
  slot[EARLY_STORE_KEY] = undefined;
  return early.res;
}

/** The early response, or null when it failed or outlived the request ceiling —
 *  the caller then asks the network itself. */
async function earlyResponse(early: Promise<Response>, timeoutMs: number): Promise<Response | null> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<null>((done) => (timer = setTimeout(() => done(null), timeoutMs)));
  try {
    return await Promise.race([early.catch(() => null), late]);
  } finally {
    clearTimeout(timer);
  }
}

/** At most this many slow-call reports a minute from one tab: a dead network must not become a flood. */
const SLOW_REPORTS_PER_MIN = 10;
let slowReports: number[] = [];

/**
 * 119L: tell the Worker about a call that was slow, timed out or never got an
 * answer — the one thing its own logs cannot see, since a request the browser
 * gave up on is cancelled at the edge too. Fire-and-forget, never retried.
 */
function reportSlowCall(path: string, method: string, outcome: CallOutcome): void {
  if (path === SLOW_REPORT_PATH || typeof window === "undefined" || navigator.onLine === false) return;
  const kind = slowKindFor(outcome);
  if (!kind) return;
  const now = Date.now();
  slowReports = slowReports.filter((t) => now - t < 60_000);
  if (slowReports.length >= SLOW_REPORTS_PER_MIN) return;
  slowReports.push(now);
  try {
    void fetch(`/api${SLOW_REPORT_PATH}`, {
      method: "POST",
      headers: { ...(lastAuthHeaders ?? {}), "Content-Type": "application/json" },
      body: JSON.stringify({
        method,
        route: routePattern(`/api${path}`),
        kind,
        status: outcome.status,
        ms: Math.round(outcome.ms),
        screen: routePattern(window.location.pathname),
      }),
      keepalive: true,
    }).catch(() => {});
  } catch {
    /* best effort */
  }
}

async function request<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const started = Date.now();
  const answered = { status: null as number | null };
  const outcome = (error: unknown): CallOutcome => ({
    ms: Date.now() - started,
    errorCode: error === null ? null : error instanceof ApiClientError ? error.code : "network_error",
    status: error instanceof ApiClientError && error.code !== "timeout" ? error.status : answered.status,
    longByDesign: (opts.timeoutMs ?? REQUEST_TIMEOUT_MS) > REQUEST_TIMEOUT_MS,
    aborted: Boolean(opts.signal?.aborted),
    hidden: typeof document !== "undefined" && document.visibilityState === "hidden",
  });
  try {
    const data = await send<T>(path, opts, answered);
    reportSlowCall(path, opts.method ?? "GET", outcome(null));
    return data;
  } catch (e) {
    reportSlowCall(path, opts.method ?? "GET", outcome(e));
    throw e;
  }
}

async function send<T>(path: string, opts: RequestOptions, answered: { status: number | null }): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };

  if (!opts.public) await attachAuth(headers, opts.optionalAuth);
  else if (typeof window !== "undefined") Object.assign(headers, freshHeaders(window.location.search));
  if (opts.headers) Object.assign(headers, opts.headers);
  // A checkout on a custom/landing domain says which host it runs on, so the
  // buyer is sent back to that same domain after paying (125E; checked server-side).
  if (path.startsWith("/payments/")) {
    const mode = currentHostMode();
    if (mode.kind === "domain") headers["X-Storefront-Domain"] = mode.host;
  }

  const early = opts.public && (opts.method ?? "GET") === "GET" ? takeEarly(path) : null;
  const timeoutMs = opts.timeoutMs ?? REQUEST_TIMEOUT_MS;
  const res =
    (early && (await earlyResponse(early, timeoutMs))) ||
    (await fetchWithTimeout(
      `/api${path}`,
      {
        method: opts.method ?? "GET",
        headers,
        body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
      },
      timeoutMs,
      opts.signal,
    ));
  answered.status = res.status;

  if (!opts.public && !opts.optionalAuth && res.status === 401) handleAuthFailure();

  let json: ApiResponse<T>;
  try {
    json = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError({ code: "network_error", message: "Unexpected server response" }, res.status);
  }

  if (!json.ok) throw new ApiClientError(json.error, res.status);
  if ((opts.method ?? "GET") !== "GET" && !opts.public) invalidateEntries(invalidatedBy(path));
  return json.data;
}

/** One network read per remembered key at a time. */
const inflightGets = new Map<string, Promise<unknown>>();

/**
 * A dashboard read the browser may answer from memory (shared/apiCacheRules.ts):
 * fresh → no request; stale → the remembered answer now and a refresh behind
 * it, which re-renders the screen when it lands; unknown → a normal request.
 */
function cachedGet<T>(path: string, opts: Omit<RequestOptions, "method" | "body">, freshMs: number): Promise<T> {
  startApiCacheSync();
  const key = `${activeStoreId ?? ""}|${path}`;
  noteKey(key);
  const refresh = (): Promise<T> => {
    let pending = inflightGets.get(key) as Promise<T> | undefined;
    if (!pending) {
      pending = request<T>(path, { ...opts, method: "GET" })
        .then((data) => {
          writeEntry(key, data);
          return data;
        })
        .finally(() => inflightGets.delete(key));
      inflightGets.set(key, pending);
    }
    return pending;
  };
  const hit = readEntry(key);
  if (!hit) return refresh().then((data) => copyOf(data));
  if (Date.now() - hit.at >= freshMs) void refresh().catch(() => {});
  return Promise.resolve(copyOf(hit.data) as T);
}

/** Multipart upload — sends FormData (browser sets the boundary), with auth. */
async function upload<T>(path: string, formData: FormData): Promise<T> {
  const headers: Record<string, string> = {};
  await attachAuth(headers);

  const res = await fetchWithTimeout(
    `/api${path}`,
    { method: "POST", headers, body: formData },
    UPLOAD_TIMEOUT_MS,
  );

  if (res.status === 401) handleAuthFailure();

  let json: ApiResponse<T>;
  try {
    json = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError(
      { code: "network_error", message: "Unexpected server response" },
      res.status,
    );
  }
  if (!json.ok) throw new ApiClientError(json.error, res.status);
  invalidateEntries(invalidatedBy(path));
  return json.data;
}

/** Raw bytes in a PUT body (one part of a large upload), with auth. */
async function putBinary<T>(path: string, body: Blob, signal?: AbortSignal): Promise<T> {
  const headers: Record<string, string> = { "Content-Type": "application/octet-stream" };
  await attachAuth(headers);
  const res = await fetchWithTimeout(`/api${path}`, { method: "PUT", headers, body }, UPLOAD_TIMEOUT_MS, signal);
  if (res.status === 401) handleAuthFailure();
  let json: ApiResponse<T>;
  try {
    json = (await res.json()) as ApiResponse<T>;
  } catch {
    throw new ApiClientError({ code: "network_error", message: "Unexpected server response" }, res.status);
  }
  if (!json.ok) throw new ApiClientError(json.error, res.status);
  return json.data;
}

/** Authenticated binary fetch — for streaming endpoints behind a Bearer token
 *  (e.g. an admin-only receipt image the browser can't load via <img src>). */
async function blob(path: string): Promise<Blob> {
  const headers: Record<string, string> = {};
  await attachAuth(headers);

  const res = await fetchWithTimeout(`/api${path}`, { headers }, UPLOAD_TIMEOUT_MS);
  if (res.status === 401) handleAuthFailure();
  if (!res.ok) {
    throw new ApiClientError({ code: "network_error", message: "Could not load file" }, res.status);
  }
  return res.blob();
}

export const api = {
  upload,
  putBinary,
  blob,
  get: <T>(path: string, opts?: Omit<RequestOptions, "method" | "body">) => {
    const rule = opts?.optionalAuth || opts?.signal ? null : apiCacheRule(path);
    return rule ? cachedGet<T>(path, opts ?? {}, rule.freshMs) : request<T>(path, { ...opts, method: "GET" });
  },
  post: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method">) =>
    request<T>(path, { ...opts, method: "POST", body }),
  put: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method">) =>
    request<T>(path, { ...opts, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, opts?: Omit<RequestOptions, "method">) =>
    request<T>(path, { ...opts, method: "PATCH", body }),
  delete: <T>(path: string, opts?: Omit<RequestOptions, "method" | "body">) =>
    request<T>(path, { ...opts, method: "DELETE" }),
};
